import { useState, useEffect } from 'react'
import { BackofficeLayout } from '../../components/layout/BackofficeLayout'
import { Modal } from '@eshop/shared'
import { productsApi, categoriesApi } from '@eshop/shared'
import { useLanguage } from '@eshop/shared'

export function ProductsPage() {
  const { t, language } = useLanguage()
  const [products, setProducts] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<any | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadProducts()
    loadCategories()
  }, [language]) // Reload products when language changes

  const loadProducts = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await productsApi.vendor.getAll()
      setProducts(response.results)
    } catch (err: any) {
      setError(err.response?.data?.detail || t('error'))
      console.error('Error loading products:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getAll()
      setCategories(response.results)
    } catch (err) {
      console.error('Error loading categories:', err)
    }
  }

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && product.in_stock) ||
                         (statusFilter === 'inactive' && !product.in_stock)
    return matchesSearch && matchesStatus
  })

  const handleEdit = async (product: any) => {
    try {
      const fullProduct = await productsApi.getById(product.id)
      setEditingProduct(fullProduct)
      setIsModalOpen(true)
    } catch (err) {
      console.error('Error loading product:', err)
      alert(t('error'))
    }
  }

  const handleDelete = async (productId: number) => {
    if (confirm(t('deleteConfirm'))) {
      try {
        await productsApi.delete(productId)
        await loadProducts()
      } catch (err) {
        console.error('Error deleting product:', err)
        alert(t('errorDeletingProduct'))
      }
    }
  }

  const handleCreateNew = () => {
    setEditingProduct(null)
    setIsModalOpen(true)
  }

  if (loading) {
    return (
      <BackofficeLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('loadingProducts')}</div>
        </div>
      </BackofficeLayout>
    )
  }

  if (error) {
    return (
      <BackofficeLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={loadProducts}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            {t('retry')}
          </button>
        </div>
      </BackofficeLayout>
    )
  }

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">{t('products')}</h1>
            <p className="text-neutral-500 mt-1">{t('manageProducts')}</p>
          </div>
          <button
            onClick={handleCreateNew}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium shadow-sm"
          >
            + {t('newProduct')}
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-5">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder={t('searchByName')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">{t('allStatuses')}</option>
              <option value="active">{t('withStock')}</option>
              <option value="inactive">{t('noStock')}</option>
            </select>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('product')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('category')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('price')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      {t('noProductsFound')}
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-blue-50 transition-colors">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center">
                          {(product.primary_image || product.image_url) && (
                            <img
                              key={product.primary_image || product.image_url}
                              src={product.primary_image || product.image_url}
                              alt={product.name}
                              className="w-10 h-10 rounded object-cover mr-3 shadow-sm"
                            />
                          )}
                          <div className="text-sm font-medium text-gray-900">
                            {product.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-600">
                        {product.category_name || '-'}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          ${parseFloat(product.price).toFixed(2)}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                          product.in_stock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {product.in_stock ? t('inStockLabel') : t('outOfStockLabel')}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEdit(product)}
                          className="text-blue-600 hover:text-blue-800 mr-3 font-medium"
                        >
                          {t('view')}
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="text-red-600 hover:text-red-800 font-medium"
                        >
                          {t('delete')}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-blue-800">
            {t('showing')} {filteredProducts.length} {t('of')} {products.length} {t('products').toLowerCase()}
          </p>
        </div>
      </div>

      {/* Product Form Modal */}
      {isModalOpen && (
        <ProductFormModal
          product={editingProduct}
          categories={categories}
          onClose={() => setIsModalOpen(false)}
          onSave={async (product) => {
            try {
              if (editingProduct) {
                await productsApi.update(editingProduct.id, product)
              } else {
                await productsApi.create(product)
              }
              await loadProducts()
              setIsModalOpen(false)
            } catch (err: any) {
              console.error('Error saving product:', err)
              alert(err.response?.data?.detail || t('errorSavingProduct'))
            }
          }}
        />
      )}
    </BackofficeLayout>
  )
}

interface ProductFormModalProps {
  product: any | null
  categories: any[]
  onClose: () => void
  onSave: (product: any) => void
}

function ProductFormModal({ product, categories, onClose, onSave }: ProductFormModalProps) {
  const { t } = useLanguage()
  const [activeTab, setActiveTab] = useState<'es' | 'en'>('es')
  const [uploadingImage, setUploadingImage] = useState(false)

  // Initialize images from product
  const [images, setImages] = useState<Array<{
    id?: number
    image_url: string
    order: number
    is_primary: boolean
  }>>(product?.images || [])

  // Initialize translations from product if editing
  const [translations, setTranslations] = useState({
    es: {
      name: product?.translations?.find((t: any) => t.language === 'es')?.name || product?.name || '',
      description: product?.translations?.find((t: any) => t.language === 'es')?.description || product?.description || ''
    },
    en: {
      name: product?.translations?.find((t: any) => t.language === 'en')?.name || '',
      description: product?.translations?.find((t: any) => t.language === 'en')?.description || ''
    }
  })

  const [formData, setFormData] = useState({
    price: product?.price || '',
    category: product?.category || '',
    stock: product?.stock || 0,
    image_url: product?.image_url || '',
  })

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']

    try {
      setUploadingImage(true)
      const uploadedImages = []

      // Upload each file using the API client
      for (let i = 0; i < files.length; i++) {
        const file = files[i]

        // Validate file type
        if (!allowedTypes.includes(file.type)) {
          alert(`${t('invalidFileType')}: ${file.name}`)
          continue
        }

        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
          alert(`${t('fileSizeTooLarge')}: ${file.name}`)
          continue
        }

        // Use the productsApi to upload image (handles auth automatically)
        const data = await productsApi.vendor.uploadImage(file)

        uploadedImages.push({
          image_url: data.url,
          order: images.length + uploadedImages.length,
          is_primary: images.length === 0 && uploadedImages.length === 0 // First image is primary
        })
      }

      // Add new images to the list
      setImages([...images, ...uploadedImages])

      // Clear the input
      e.target.value = ''
    } catch (err) {
      console.error('Error uploading image:', err)
      alert(t('errorUploadingImage'))
    } finally {
      setUploadingImage(false)
    }
  }

  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index)
    // Reorder remaining images
    const reorderedImages = newImages.map((img, i) => ({
      ...img,
      order: i,
      // If we removed the primary image, make the first one primary
      is_primary: img.is_primary || (i === 0 && images[index].is_primary)
    }))
    setImages(reorderedImages)
  }

  const handleSetPrimary = (index: number) => {
    const newImages = images.map((img, i) => ({
      ...img,
      is_primary: i === index
    }))
    setImages(newImages)
  }

  const handleMoveImage = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === images.length - 1)
    ) {
      return
    }

    const newImages = [...images]
    let targetIndex: number
    if (direction === 'up') {
      targetIndex = index - 1
    } else {
      targetIndex = index + 1
    }

    // Swap
    const temp = newImages[index]
    newImages[index] = newImages[targetIndex]
    newImages[targetIndex] = temp

    // Update order
    const reorderedImages = newImages.map((img, i) => ({
      ...img,
      order: i
    }))

    setImages(reorderedImages)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Combine formData with translations (only include non-empty translations)
    const translationsArray = []

    // Always include Spanish (required)
    if (translations.es.name && translations.es.description) {
      translationsArray.push({
        language: 'es',
        name: translations.es.name,
        description: translations.es.description
      })
    }

    // Only include English if both fields are filled
    if (translations.en.name && translations.en.description) {
      translationsArray.push({
        language: 'en',
        name: translations.en.name,
        description: translations.en.description
      })
    }

    // Prepare images data (remove id for new images)
    const imagesData = images.map(({ id, ...rest }) => rest)

    const productData = {
      ...formData,
      translations: translationsArray,
      images: imagesData
    }

    onSave(productData)
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={product ? t('editProduct') : t('createProduct')}
      maxWidth="2xl"
    >
      <div className="max-h-[70vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Language Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setActiveTab('es')}
                className={`pb-3 px-4 font-medium transition-colors ${
                  activeTab === 'es'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                🇪🇸 Español
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('en')}
                className={`pb-3 px-4 font-medium transition-colors ${
                  activeTab === 'en'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                🇺🇸 English
              </button>
            </div>
          </div>

          {/* Translation Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('name')} * ({activeTab === 'es' ? 'Español' : 'English'})
            </label>
            <input
              type="text"
              required={activeTab === 'es'}
              value={translations[activeTab].name}
              onChange={(e) => setTranslations({
                ...translations,
                [activeTab]: { ...translations[activeTab], name: e.target.value }
              })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('namePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('description')} * ({activeTab === 'es' ? 'Español' : 'English'})
            </label>
            <textarea
              required={activeTab === 'es'}
              value={translations[activeTab].description}
              onChange={(e) => setTranslations({
                ...translations,
                [activeTab]: { ...translations[activeTab], description: e.target.value }
              })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('descriptionPlaceholder')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('price')} *
              </label>
              <input
                type="number"
                required
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('pricePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('stock')} *
              </label>
              <input
                type="number"
                required
                min="0"
                value={formData.stock}
                onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('stockPlaceholder')}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('category')} *
            </label>
            <select
              required
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">{t('selectCategory')}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('productImages')} ({images.length})
            </label>
            <div className="space-y-3">
              {/* Upload Button */}
              <label>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  className="hidden"
                  multiple
                />
                <div
                  className={`cursor-pointer px-4 py-2 border-2 border-dashed rounded-lg text-center transition-colors ${
                    uploadingImage
                      ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                      : 'border-blue-300 hover:border-blue-400 hover:bg-blue-50'
                  }`}
                >
                  {uploadingImage ? (
                    <span className="text-gray-500">{t('uploading')}...</span>
                  ) : (
                    <span className="text-blue-600">
                      📁 {t('selectImages')}
                    </span>
                  )}
                </div>
              </label>

              {/* Images Gallery */}
              {images.length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  {images.map((image, index) => (
                    <div
                      key={index}
                      className={`relative rounded-lg border-2 overflow-hidden ${
                        image.is_primary ? 'border-green-500' : 'border-gray-200'
                      }`}
                    >
                      <img
                        src={image.image_url}
                        alt={`Product ${index + 1}`}
                        className="w-full h-32 object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />

                      {/* Primary Badge */}
                      {image.is_primary && (
                        <div className="absolute top-1 left-1 bg-green-500 text-white text-xs px-2 py-0.5 rounded">
                          {t('primary')}
                        </div>
                      )}

                      {/* Controls */}
                      <div className="absolute top-1 right-1 flex gap-1">
                        {/* Move Up */}
                        {index > 0 && (
                          <button
                            type="button"
                            onClick={() => handleMoveImage(index, 'up')}
                            className="bg-blue-600 text-white text-xs px-2 py-1 rounded hover:bg-blue-700"
                            title={t('moveUp')}
                          >
                            ↑
                          </button>
                        )}

                        {/* Move Down */}
                        {index < images.length - 1 && (
                          <button
                            type="button"
                            onClick={() => handleMoveImage(index, 'down')}
                            className="bg-blue-600 text-white text-xs px-2 py-1 rounded hover:bg-blue-700"
                            title={t('moveDown')}
                          >
                            ↓
                          </button>
                        )}

                        {/* Delete */}
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="bg-red-600 text-white text-xs px-2 py-1 rounded hover:bg-red-700"
                          title={t('remove')}
                        >
                          ✕
                        </button>
                      </div>

                      {/* Set as Primary */}
                      {!image.is_primary && (
                        <button
                          type="button"
                          onClick={() => handleSetPrimary(index)}
                          className="absolute bottom-1 left-1 right-1 bg-black bg-opacity-60 text-white text-xs py-1 rounded hover:bg-opacity-80"
                        >
                          {t('setAsPrimary')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <p className="text-xs text-gray-500">
                {t('allowedFormats')}: JPG, PNG, GIF, WebP (max 10MB). {t('multipleImagesHint')}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {product ? t('update') : t('create')}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
}
